
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUserStore } from "@/store/userStore";
import { useTranslation } from "react-i18next";
import { getPrimaryRole } from "./useUserRoles";
import { useAppName } from "./useAppName";
import { checkSuperAdmin } from "@/services/edgeFunctionWrappers/userRolesWrapper";

export const useLoginAuth = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["auth", "common"]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingRole, setIsCheckingRole] = useState(false);
  const { setRole } = useUserStore();
  const appName = useAppName();
  

  const checkUserRole = async (userId: string) => {
    if (isCheckingRole) {
      console.log('checkUserRole skipped: already running');
      return;
    }
    setIsCheckingRole(true);
    const startedAt = Date.now();
    try {
      console.log("Fetching roles for user ID:", userId, 'START');
      
      // ✅ 0. Vérifier d'abord si c'est un super admin
      const isSuperAdmin = await checkSuperAdmin(userId);

      if (isSuperAdmin) {
        console.log("Super admin detected, redirecting to super admin dashboard");
        setRole('super_admin');
        navigate("/super-admin/dashboard", { replace: true });
        return;
      }
      
      // ✅ 1. Appeler l'edge function pour récupérer les gyms accessibles
      console.log('[useLoginAuth] Validating app access for:', { userId, appName });

      const { data: gymAccessData, error: gymAccessError } = await supabase.functions.invoke(
        'get-accessible-gyms',
        {
          body: { 
            user_id: userId, 
            app_name: appName 
          }
        }
      );

      if (gymAccessError) {
        console.error('[useLoginAuth] Error validating gym access:', gymAccessError);
        toast.error(t("loginFailed", { ns: "auth" }));
        return;
      }

      if (!gymAccessData.success) {
        // Gestion des erreurs spécifiques
        if (gymAccessData.error === 'no_gym_assigned') {
          console.log('[useLoginAuth] No gym assigned to user');
          toast.error(t("noGymAssigned", { ns: "auth" }));
        } else if (gymAccessData.error === 'account_not_exist_for_app') {
          console.log('[useLoginAuth] Account not accessible from app:', appName);
          toast.error(t("accountNotExistForApp", { ns: "auth" }));
        } else {
          toast.error(t("loginFailed", { ns: "auth" }));
        }
        return;
      }

      const assignments = gymAccessData.gyms; // Array<{ gym_id: string, is_primary_gym: boolean }>

      if (assignments.length === 0) {
        console.log('[useLoginAuth] No accessible gyms found');
        toast.error(t("accountNotExistForApp", { ns: "auth" }));
        return;
      }

      // ✅ 2. Trouver le gym primaire
      const primaryGym = assignments.find(a => a.is_primary_gym) || assignments[0];
      const gymId = primaryGym.gym_id;

      console.log('[useLoginAuth] Primary gym selected:', gymId);

      // ✅ 2. Charger les rôles pour CE gym spécifique
      const rolesPromise = supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('gym_id', gymId);

      const withTimeout = <T,>(p: Promise<T>, ms: number) =>
        new Promise<T>((resolve, reject) => {
          const id = setTimeout(() => reject(new Error('roles_timeout')), ms);
          p.then((res) => { clearTimeout(id); resolve(res); })
           .catch((err) => { clearTimeout(id); reject(err); });
        });

      const { data: rolesData, error: rolesError } = await withTimeout(rolesPromise as any, 10000) as any;

      console.log("Fetching roles END in", Date.now() - startedAt, "ms");

      if (rolesError) {
        console.error("Roles Fetch Error:", rolesError);
        setRole('athlete');
        navigate("/athlete/dashboard", { replace: true });
        return;
      }

      const userRoles = (rolesData || []).map((r: any) => r.role);
      
      if (userRoles.length === 0) {
        console.log("No roles found for gym, defaulting to athlete");
        setRole('athlete');
        navigate("/athlete/dashboard", { replace: true });
        return;
      }
      
      // ✅ 3. Déterminer le rôle pour ce gym
      const primaryRole = getPrimaryRole(userRoles as any[]);
      setRole(primaryRole);
      
      // ✅ 4. Navigation basée sur le rôle
      if (primaryRole === 'admin') {
        navigate("/admin/dashboard", { replace: true });
      } else if (primaryRole === 'coach') {
        navigate("/coach/schedule", { replace: true });
      } else {
        navigate("/athlete/dashboard", { replace: true });
      }
    } catch (error) {
      if ((error as Error).message === 'roles_timeout') {
        console.error('Roles query timeout after 10s');
        setRole('athlete');
        navigate("/athlete/dashboard", { replace: true });
      } else {
        console.error("Unexpected error checking role:", error);
      }
    } finally {
      setIsCheckingRole(false);
    }
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      console.log("Login attempt with email:", email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      // Log detailed authentication results
      console.log("Authentication Data:", { 
        user: data.user ? data.user.id : 'No User', 
        session: data.session ? 'Active' : 'No Session' 
      });

      if (error) {
        console.error("Login Error Details:", error);
        toast.error(t("loginFailed", { ns: "auth" }));
        throw error;
      }

      if (data.user) {
        console.log("Login successful. User ID:", data.user.id);
        toast.success(t("loginSuccess", { ns: "auth" }));

        await checkUserRole(data.user.id);
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return { 
    isLoading, 
    handleLogin,
    checkUserRole,
    isCheckingRole
  };
};
